import { useEffect, useMemo, useRef, useState, type ClipboardEvent, type ChangeEvent } from "react";
import { Alert, Button, Checkbox, Input, Pagination, Select, Spin, Tag } from "@arco-design/web-react";
import { Download, FileSpreadsheet, Play, Save } from "lucide-react";

import { useCurrentAuthUser } from "../../auth";
import { canUsePermission } from "../../../lib/auth";
import { CONTACT_IMPORT_ACCEPT } from "../config";
import { useCrmContactImportRuntime } from "../runtime/useCrmContactImportRuntime";
import {
  describeContactImportIssue,
  getContactImportExecutionStatusMeta,
  getVisibleContactImportIssues,
  isContactImportRowExecutable,
  paginateContactImportRows,
} from "../service/crmContactImportService";
import type {
  ContactImportAction,
  ContactImportExecutionRow,
  ContactImportRow,
} from "../types";
import "./CrmContactImportPage.css";
import "../../../shared/ui/crmListPageLayout.css";

const TextArea = Input.TextArea;

/** CRM 联系人批量导入工作台。 */
export function CrmContactImportFeaturePage() {
  const runtime = useCrmContactImportRuntime();
  const currentUser = useCurrentAuthUser().data;
  const canParse = canUsePermission(currentUser, "crm.contact_import:parse");
  const canEdit = canUsePermission(currentUser, "crm.contact_import:edit");
  const canExecute = canUsePermission(currentUser, "crm.contact_import:execute");
  const [pasteText, setPasteText] = useState("");
  const [pasteHtml, setPasteHtml] = useState<string | null>(null);
  const [previewPage, setPreviewPage] = useState(1);
  const [previewPageSize, setPreviewPageSize] = useState(100);
  const topScrollRef = useRef<HTMLDivElement>(null);
  const gridWrapRef = useRef<HTMLDivElement>(null);
  const tableRef = useRef<HTMLTableElement>(null);
  const [scrollMetrics, setScrollMetrics] = useState({ width: 0, viewport: 0 });
  const previewPageCount = Math.max(1, Math.ceil(runtime.rows.length / previewPageSize));
  const currentPreviewPage = Math.min(previewPage, previewPageCount);
  const previewRows = useMemo(
    () => paginateContactImportRows(runtime.rows, currentPreviewPage, previewPageSize),
    [currentPreviewPage, previewPageSize, runtime.rows],
  );

  useEffect(() => setPreviewPage(1), [runtime.task?.id]);

  useEffect(() => {
    const grid = gridWrapRef.current;
    const table = tableRef.current;
    if (!grid || !table) return;
    const updateMetrics = () => setScrollMetrics({ width: grid.scrollWidth, viewport: grid.clientWidth });
    updateMetrics();
    const observer = typeof ResizeObserver === "undefined" ? undefined : new ResizeObserver(updateMetrics);
    observer?.observe(grid);
    observer?.observe(table);
    window.addEventListener("resize", updateMetrics);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", updateMetrics);
    };
  }, [currentPreviewPage, previewPageSize, runtime.rows.length]);

  const syncHorizontalScroll = (source: HTMLDivElement) => {
    for (const target of [topScrollRef.current, gridWrapRef.current]) {
      if (target && target !== source && target.scrollLeft !== source.scrollLeft) {
        target.scrollLeft = source.scrollLeft;
      }
    }
  };

  const handleFile = (event: ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (file) void runtime.uploadFile(file);
    event.target.value = "";
  };

  const handlePaste = (event: ClipboardEvent<HTMLTextAreaElement>) => {
    const html = event.clipboardData.getData("text/html");
    if (html) setPasteHtml(html);
  };

  return (
    <main className="crm-list-page crm-contact-import-page">
      {runtime.error ? <Alert className="crm-list-alert" type="error" content={runtime.error} closable /> : null}
      {runtime.result ? (
        <section className="crm-list-panel">
          <div className="crm-list-section-header">
            <h2>处理结果</h2>
          </div>
          <div className="crm-list-panel-body crm-contact-import-result-summary" aria-label="导入处理结果">
          <span>总行 {runtime.result.total}</span>
          <span>已新增 {runtime.result.created}</span>
          <span>已更新 {runtime.result.updated}</span>
          <span>已记动态 {runtime.result.recorded}</span>
          <span>重复 {runtime.result.duplicate}</span>
          <span>阻断 {runtime.result.blocked}</span>
          <span>跳过 {runtime.result.skipped}</span>
          <span>失败 {runtime.result.failed}</span>
          </div>
        </section>
      ) : null}

      <section className="crm-list-panel crm-contact-import-paste-card">
        <div className="crm-list-section-header">
          <h2>粘贴导入</h2>
        </div>
        <div className="crm-list-panel-body crm-contact-import-paste-body">
        <TextArea
          disabled={!canParse}
          value={pasteText}
          onChange={setPasteText}
          onPaste={handlePaste}
          placeholder="可直接从 Excel 复制单元格后粘贴到这里；优先保留 HTML 表格结构。"
          autoSize={{ minRows: 3, maxRows: 7 }}
        />
        <Button
          type="primary"
          disabled={!canParse || !pasteText.trim()}
          onClick={() => void runtime.parsePaste(pasteHtml, pasteText)}
        >
          解析粘贴内容
        </Button>
        </div>
      </section>

      {runtime.task ? (
        <section className="crm-list-panel">
          <div className="crm-list-section-header">
            <h2>任务摘要</h2>
          </div>
          <div className="crm-list-panel-body crm-contact-import-summary">
          <span>任务：{runtime.task.taskNo}</span>
          <span>状态：{runtime.task.status}</span>
          <span>总行：{runtime.rows.length}</span>
          <span>库里已有：{runtime.task.validationSummary.system_exists ?? 0}</span>
          <span>待补电话：{runtime.task.validationSummary.phone_supplement ?? 0}</span>
          <span>阻断：{runtime.task.validationSummary.blocked ?? 0}</span>
          </div>
        </section>
      ) : null}

      <section className="crm-list-panel">
        <div className="crm-list-section-header">
          <h2>导入预览</h2>
          <div className="crm-list-table-actions crm-contact-import-actions">
            <Button icon={<Download size={16} />} onClick={() => void runtime.downloadTemplate()}>
              下载标准模板
            </Button>
            {runtime.result ? (
              <Button
                type="primary"
                className="crm-contact-import-download-unimported"
                icon={<Download size={16} />}
                onClick={() => void runtime.downloadUnimportedRows()}
              >
                下载未导入名单
              </Button>
            ) : null}
            <label className="crm-contact-import-upload">
              <FileSpreadsheet size={16} /> 上传文件
              <input
                aria-label="上传文件"
                type="file"
                accept={CONTACT_IMPORT_ACCEPT}
                disabled={!canParse}
                onChange={handleFile}
              />
            </label>
            <Button
              icon={<Save size={16} />}
              disabled={!canEdit || runtime.busy || !runtime.task || !runtime.rows.length}
              onClick={() => void runtime.saveRows()}
            >
              保存并重新校验
            </Button>
            <Button
              type="primary"
              status="success"
              icon={<Play size={16} />}
              disabled={!canExecute || runtime.busy || !runtime.task?.previewDigest || runtime.executableCount === 0}
              onClick={() => void runtime.execute()}
            >
              导入有效行（{runtime.executableCount}）
            </Button>
          </div>
        </div>
        <div className="crm-list-panel-body">
      {runtime.rows.length ? (
        <p className="crm-contact-import-edit-hint">
          可直接修改学校名称、二级学院、系、专业及联系人资料；修改后点击“保存并重新校验”，系统重新匹配学校和联系人，通过校验后再导入。
          {runtime.task && !runtime.task.previewDigest ? <strong> 有修改待校验，暂不可导入。</strong> : null}
        </p>
      ) : null}
      <Spin loading={runtime.busy} block>
        {runtime.rows.length ? (
          <div
            ref={topScrollRef}
            role="region"
            aria-label="预览列表顶部横向滚动条"
            tabIndex={0}
            className="crm-contact-import-top-scroll"
            style={{ width: scrollMetrics.viewport || "100%" }}
            onScroll={(event) => syncHorizontalScroll(event.currentTarget)}
          >
            <div style={{ width: scrollMetrics.width, height: 1 }} />
          </div>
        ) : null}
        <div
          ref={gridWrapRef}
          role="region"
          aria-label="预览列表及底部横向滚动条"
          tabIndex={0}
          className="crm-contact-import-grid-wrap"
          onScroll={(event) => syncHorizontalScroll(event.currentTarget)}
        >
          {runtime.rows.length ? (
            <table ref={tableRef} className="crm-contact-import-grid">
              <thead>
                <tr>
                  <th>选择</th>
                  <th>源行</th>
                  <th className="sticky-exists">系统是否有</th>
                  <th>导入动作</th>
                  <th>学校名称</th>
                  <th>二级学院</th>
                  <th>系</th>
                  <th>专业</th>
                  <th>联系人姓名</th>
                  <th>手机号</th>
                  <th>备用电话</th>
                  <th>微信号</th>
                  <th>所属主销售</th>
                  <th>职务</th>
                  <th>联系人备注</th>
                  <th>动态时间</th>
                  <th>动态内容</th>
                  <th>处理结果</th>
                </tr>
              </thead>
              <tbody>
                {previewRows.map((row) => (
                  <PreviewRow key={row.id} row={row} runtime={runtime} canEdit={canEdit && !runtime.busy} />
                ))}
              </tbody>
            </table>
          ) : (
            <div className="crm-contact-import-empty">上传文件或粘贴 Excel 内容后生成可编辑预览。</div>
          )}
        </div>
      </Spin>
      {runtime.rows.length ? (
        <Pagination
          total={runtime.rows.length}
          current={currentPreviewPage}
          pageSize={previewPageSize}
          sizeOptions={[20, 50, 100]}
          sizeCanChange
          selectProps={{ "aria-label": "每页行数" }}
          showTotal
          showJumper
          onChange={(page, size) => {
            setPreviewPage(size === previewPageSize ? page : 1);
            setPreviewPageSize(size);
            if (gridWrapRef.current) gridWrapRef.current.scrollTop = 0;
          }}
          className="crm-contact-import-pagination"
        />
      ) : null}
        </div>
      </section>
    </main>
  );
}

interface PreviewRowProps {
  row: ContactImportRow;
  runtime: ReturnType<typeof useCrmContactImportRuntime>;
  canEdit: boolean;
}

/** 渲染单条可编辑预览行。 */
function PreviewRow({ row, runtime, canEdit }: PreviewRowProps) {
  const contact = row.normalized.contact;
  const organization = row.normalized.organization;
  const record = row.normalized.record;
  const assignment = row.normalized.assignment ?? {};
  const systemExists = getSystemExistsTagMeta(row);
  const canSelect = canEdit && isContactImportRowExecutable(row);
  const executionMeta = getContactImportExecutionStatusMeta(
    row.executionResult?.status,
  );
  const rowStatusClass = runtime.result
    ? executionMeta.imported ? "status-imported" : "status-unimported"
    : `status-${row.validationStatus}`;
  return (
    <tr className={rowStatusClass}>
      <td>
        <Checkbox
          checked={row.selected}
          disabled={!canSelect}
          onChange={(checked) => runtime.toggleRow(row.id, checked)}
        />
      </td>
      <td>{row.sourceSheet} · {row.sourceRowNo}</td>
      <td className={`sticky-exists${systemExists.hasIssues ? " has-issues" : ""}`}>
        <Tag color={systemExists.color}>{systemExists.label}</Tag>
        {row.matchResult.matched_fields?.length ? (
          <small>{row.matchResult.matched_fields.join("；")}</small>
        ) : null}
      </td>
      <td>
        <Select
          disabled={!canEdit || (row.matchResult.system_exists !== "no" && !row.matchResult.allowed_actions?.includes("update"))}
          aria-label={`导入动作，第 ${row.sourceRowNo} 行`}
          value={row.actionType}
          style={{ width: 132 }}
          onChange={(value) => runtime.updateAction(row.id, value as ContactImportAction)}
        >
          <Select.Option value="create" disabled={row.matchResult.create_blocked}>新增联系人</Select.Option>
          <Select.Option value="update" disabled={!row.matchResult.allowed_actions?.includes("update")}>补充电话</Select.Option>
          <Select.Option value="skip">跳过</Select.Option>
        </Select>
      </td>
      <Editable label={`学校名称，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={organization.school_name} onChange={(value) => runtime.updateCell(row.id, "organization", "school_name", value)} />
      <Editable label={`二级学院，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={organization.college_name} onChange={(value) => runtime.updateCell(row.id, "organization", "college_name", value)} />
      <Editable label={`系，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={organization.department_name} onChange={(value) => runtime.updateCell(row.id, "organization", "department_name", value)} />
      <Editable label={`专业，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={organization.major_name} onChange={(value) => runtime.updateCell(row.id, "organization", "major_name", value)} />
      <Editable label={`联系人姓名，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={contact.contact_name} onChange={(value) => runtime.updateCell(row.id, "contact", "contact_name", value)} />
      <Editable label={`手机号，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={contact.mobile} onChange={(value) => runtime.updateCell(row.id, "contact", "mobile", value)} />
      <Editable label={`备用电话，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={contact.backup_mobile} onChange={(value) => runtime.updateCell(row.id, "contact", "backup_mobile", value)} />
      <Editable label={`微信号，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={contact.wechat} onChange={(value) => runtime.updateCell(row.id, "contact", "wechat", value)} />
      <Editable label={`所属主销售，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={assignment.primary_owner_name} onChange={(value) => runtime.updateCell(row.id, "assignment", "primary_owner_name", value)} />
      <Editable label={`职务，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={contact.position_name} onChange={(value) => runtime.updateCell(row.id, "contact", "position_name", value)} />
      <Editable label={`联系人备注，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={contact.remark} onChange={(value) => runtime.updateCell(row.id, "contact", "remark", value)} />
      <Editable label={`动态发生时间，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={record.recorded_at} onChange={(value) => runtime.updateCell(row.id, "record", "recorded_at", value)} placeholder="2026-07-19 10:00" />
      <Editable label={`动态内容，第 ${row.sourceRowNo} 行`} disabled={!canEdit} value={record.content} onChange={(value) => runtime.updateCell(row.id, "record", "content", value)} />
      <ExecutionResultCell result={row.executionResult} />
    </tr>
  );
}

/** 计算“系统是否有”列的合并标签；有问题时强制展示红色原因。 */
function getSystemExistsTagMeta(row: ContactImportRow): {
  color: string;
  label: string;
  hasIssues: boolean;
} {
  const issueText = getVisibleContactImportIssues(row)
    .map((issue) => describeContactImportIssue(issue.code))
    .join("；");
  if (issueText) {
    return {
      color: "red",
      label: `${row.matchResult.display_text} · ${issueText}`,
      hasIssues: true,
    };
  }
  return {
    color:
      row.actionType === "update" && row.matchResult.allowed_actions?.includes("update")
        ? "blue"
        : row.matchResult.system_exists === "yes"
        ? "red"
        : row.matchResult.system_exists === "pending"
          ? "orange"
          : "green",
    label: row.matchResult.display_text,
    hasIssues: false,
  };
}

/** 展示单行最终结果和后续处理说明。 */
function ExecutionResultCell({ result }: { result?: ContactImportExecutionRow }) {
  if (!result) return <td className="execution-result-cell"><Tag color="orange">未导入（未处理）</Tag></td>;
  const meta = getContactImportExecutionStatusMeta(result.status);
  return (
    <td className="execution-result-cell">
      <Tag color={meta.color}>{meta.label}</Tag>
      {result.isDuplicate &&
      ["created", "updated", "recorded", "skipped"].includes(result.status) ? (
        <Tag color="orange">重复已处理</Tag>
      ) : null}
      <small>{result.message}</small>
      {result.contactId ? <small>联系人 ID：{result.contactId}</small> : null}
    </td>
  );
}

/** 渲染通用文本单元格编辑器。 */
function Editable({
  label,
  value,
  onChange,
  placeholder,
  disabled = false,
}: {
  label: string;
  value: unknown;
  onChange: (value: string) => void;
  placeholder?: string;
  disabled?: boolean;
}) {
  return (
    <td>
      <Input
        aria-label={label}
        disabled={disabled}
        value={typeof value === "string" || typeof value === "number" ? String(value) : ""}
        placeholder={placeholder}
        onChange={onChange}
      />
    </td>
  );
}
